import RdnaSmartTable, { RdnaSmartTableRef } from '@ringdna/common/src/components/RdnaTable/RdnaSmartTable'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { User } from '../../../store/api/users-api'
import { Column } from 'react-table'
import { getFormattedFullDate } from '@ringdna/common/src/utils/timeUtils'
import { UserLicenses } from '@ringdna/common/src/types/user-settings'
import RdnaText from '@ringdna/common/src/components/RdnaText'
import styled from 'styled-components'
import { createSimpleOptions } from '@ringdna/common/src/components/RdnaFormElements/_helpers'
import {
  ALL_LICENSES,
  getPaginationProps,
  License,
  NO_RESULT_STATE,
  Option,
  PAGE_SIZE,
  UserStatus,
  userStatusOptions
} from './utils'
import ExportUsersButton from './export/ExportUsersButton'
import { flags, isFlagged } from '@ringdna/common/src/utils/feature-flag/featureFlag'
import RdnaSelect from '@ringdna/common/src/components/RdnaFormElements/RdnaSelect'

type Props = {
  payload: any | undefined
  onQueryState: any
  loading: boolean
  userStatusFilter: UserStatus
  handleUserStatusFilterChange: (userStatusValue: UserStatus) => void
  licenseFilter: string[]
  handleLicenseFilterChange: (licenses: string[], child: any) => void
  selectedAccountId: number | null
  globalFilter: string
}

export default function UsersTable({
  payload,
  onQueryState,
  loading,
  userStatusFilter,
  handleUserStatusFilterChange,
  licenseFilter,
  handleLicenseFilterChange,
  selectedAccountId,
  globalFilter
}: Props) {
  const usersTableRef = useRef<RdnaSmartTableRef | undefined>()
  const totalRowsRef = useRef(0)
  if (payload?.total) totalRowsRef.current = payload?.total
  const [placeholderLicenses, setPlaceholderLicenses] = useState<string>()

  useEffect(() => {
    let text = 'Licenses'
    if (licenseFilter?.length > 0 && licenseFilter.includes(ALL_LICENSES)) {
      text = 'All Licenses'
    } else if (licenseFilter?.length > 0) {
      text = `Licenses (${licenseFilter.length})`
    }
    setPlaceholderLicenses(text)
  }, [licenseFilter])

  const userLicensesText = (licenses: UserLicenses) => {
    const text = []
    licenses.intelligentDialer && text.push(License.INTELLIGENT_DIALER)
    licenses.conversationAI && text.push(License.CONVERSATION_AI)
    licenses.moments && text.push(License.MOMENTS)
    licenses.guidedSelling && text.push(License.GUIDED_SELLING)
    licenses.revenueAI && text.push(License.REVENUE_AI)
    licenses.calendar && text.push(License.CALENDAR)
    licenses.admin && text.push(License.ADMIN)
    return text.join(', ')
  }

  const memoPayload = useMemo(() => {
    return payload?.results?.map((user: User) => ({
      ...user,
      userStatus: user.isDeleted ? 'Deleted' : 'Active',
      userLicenses: userLicensesText(user.licenses)
    }))
  }, [payload])

  const userLicensesOptions = () => {
    const licenses = Object.values(License)
    return [
      { label: 'Select All/Clear All', value: ALL_LICENSES },
      ...(createSimpleOptions(
        isFlagged(flags.v299_REV_2816) ? licenses : licenses.filter(l => l !== License.REVENUE_AI)
      ) as Option[])
    ]
  }

  const columns = useMemo<Column<User>[]>(
    () =>
      [
        {
          Header: 'Status',
          accessor: 'userStatus',
          disableFilters: false,
          width: 50,
          Filter: function fn() {
            return (
              <RdnaSelect
                name="user-status-filter"
                onChange={({ target }) => {
                  handleUserStatusFilterChange(target.value as UserStatus)
                  usersTableRef.current?.gotoPage(0)
                }}
                options={userStatusOptions}
                value={userStatusFilter}
              />
            )
          }
        },
        {
          Header: 'Name',
          accessor: user => user,
          Cell: function createdCell(data: { value: User }) {
            return <a href={`#users/${data.value.id}`}>{data.value.displayName}</a>
          }
        },
        {
          Header: 'Email',
          accessor: 'email',
          className: 'short'
        },
        {
          Header: 'Salesforce ID',
          accessor: 'sfUserId'
        },
        {
          Header: 'Last Login',
          accessor: 'loginDate',
          width: 100,
          Cell: (data: { value?: number }) => (data.value ? getFormattedFullDate(data.value as number) : '')
        },
        {
          Header: 'Licenses',
          accessor: 'userLicenses',
          disableSortBy: true,
          disableFilters: false,
          Cell: (data: { value?: string }) => <RdnaText className={'userLicenses'}>{data.value}</RdnaText>,
          Filter: function fn() {
            return (
              <RdnaSelect
                name={'licenses-filter'}
                multiple
                className={licenseFilter?.length > 0 ? 'placeholder-active' : 'placeholder-passive'}
                numOfRows={userLicensesOptions().length}
                showPlaceholderAsOption={false}
                displayChips={false}
                onChange={(event, child) => {
                  // @ts-ignore
                  handleLicenseFilterChange(event.target.value as string[], child)
                  usersTableRef.current?.gotoPage(0)
                }}
                options={userLicensesOptions()}
                placeholder={placeholderLicenses}
                // @ts-ignore
                value={licenseFilter}
              />
            )
          }
        },
        {
          id: 'user-export',
          accessor: user => user,
          disableFilters: false,
          disableSortBy: true,
          width: 0,
          minWidth: 0,
          Cell: function createdCell() {
            return <></>
          },
          Filter: function fn() {
            return (
              <>
                {memoPayload?.length > 0 && selectedAccountId && (
                  <ExportUsersButton
                    accountId={selectedAccountId.toString()}
                    userStatus={userStatusFilter}
                    globalFilter={globalFilter}
                  ></ExportUsersButton>
                )}
              </>
            )
          }
        }
      ] as Column<User>[],
    [
      handleLicenseFilterChange,
      handleUserStatusFilterChange,
      licenseFilter,
      selectedAccountId,
      userStatusFilter,
      placeholderLicenses
    ]
  )

  return (
    <Container>
      <RdnaSmartTable
        ref={usersTableRef}
        searchPlaceholder="Search"
        initialTableState={{
          globalFilter: '',
          sortBy: [{ id: 'createdDate', desc: true }],
          pageIndex: 0,
          pageSize: PAGE_SIZE
        }}
        columns={columns}
        data={memoPayload || []}
        noResultState={NO_RESULT_STATE}
        setQueryState={onQueryState}
        isFetching={loading}
        totalItems={totalRowsRef?.current}
        {...getPaginationProps(memoPayload, totalRowsRef?.current)}
      />
    </Container>
  )
}

const Container = styled.div`
  & > div > div:first-child {
    width: 100%;
    grid-template-columns: repeat(3, minmax(auto, 300px)) auto;
  }
  span:has(.userLicenses) {
    overflow: visible;
  }
  [role='cell'] {
    height: auto;
    min-height: ${({ theme }) => theme.spacing * 6}px;
  }
  .placeholder-active {
    span {
      color: ${({ theme }) => theme.palette.text.primary};
    }
  }

  .placeholder-passive {
    span {
      color: ${({ theme }) => theme.palette.neutral.main};
    }
  }

  .userLicenses {
    white-space: break-spaces;
  }
`
